/**
 * "Emails sent" — every email queued about one demand, from the outbox.
 *
 * Council's way to check that a message actually went out, without the global
 * email log (admin-only: it carries everyone's mail). GET /demands/{id}/emails.
 */
import { useState } from 'react'
import {
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@fluentui/react-components'
import { Mail16Regular } from '@fluentui/react-icons'
import { useQuery } from '@tanstack/react-query'
import api from '../lib/api'
import { apiErrorMessage } from '../lib/apiError'

export interface DemandEmail {
  id: number
  event?: string | null
  recipient: string
  subject: string
  status: string
  created_at: string
  sent_at?: string | null
}

export function useDemandEmails(demandId: number, enabled: boolean) {
  return useQuery({
    queryKey: ['demand-emails', demandId],
    queryFn: async () => (await api.get(`/demands/${demandId}/emails`)).data as DemandEmail[],
    enabled,
  })
}

function when(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export function DemandEmailsButton({ demandId }: { demandId: number }) {
  const [open, setOpen] = useState(false)
  const { data, isLoading, isError, error } = useDemandEmails(demandId, open)

  return (
    <>
      <Button
        appearance="subtle"
        size="small"
        icon={<Mail16Regular />}
        onClick={() => setOpen(true)}
        data-testid="demand-emails-open"
      >
        Emails sent
      </Button>
      <Dialog open={open} onOpenChange={(_, d) => setOpen(d.open)}>
        <DialogSurface style={{ maxWidth: 820 }}>
          <DialogBody>
            <DialogTitle>Emails sent about this demand</DialogTitle>
            <DialogContent>
              {isLoading && <Spinner size="small" label="Loading…" />}
              {isError && <Caption1>{apiErrorMessage(error)}</Caption1>}
              {data && data.length === 0 && <Caption1>No emails about this demand yet.</Caption1>}
              {data && data.length > 0 && (
                <Table size="small" aria-label="Emails sent about this demand" data-testid="demand-emails">
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>To</TableHeaderCell>
                      <TableHeaderCell>Subject</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>Sent</TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.map((e) => (
                      <TableRow key={e.id}>
                        <TableCell>{e.recipient}</TableCell>
                        <TableCell>{e.subject}</TableCell>
                        <TableCell>{e.status}</TableCell>
                        {/* Only a real send time: a queued or failed row has none,
                            and its queue time under "Sent" read as a delivery. */}
                        <TableCell>{e.sent_at ? when(e.sent_at) : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpen(false)}>Close</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  )
}
